<template>
  <RFIDScanModal
    v-if="rfidCodeData != null && rfidModal"
    v-model="rfidModal"
    :data="rfidCodeData"
    :login="{ code: rfidLastCode }"
    @update-table="updateTable"
  />
</template>

<script setup lang="ts">
import { ref } from "vue";
import scanner from "onscan.js";
import { useApolloClient } from "@vue/apollo-composable";
import { TCGetStampStateForRfidDocument, type TCGetStampStateForRfidQuery } from "@/graphql";
import RFIDScanModal from "./RFIDScanModal.vue";

const rfidDisabled = ref(false);
const rfidRequestPending = ref(false);

const rfidModal = ref(false);
const rfidCodeData = ref<TCGetStampStateForRfidQuery["TCStampPreacquisitionData"] | null>(null);
const rfidLastCode = ref("");
const { resolveClient } = useApolloClient();
let latestRequestId = 0;

function disableRFID() {
  latestRequestId += 1;
  rfidDisabled.value = true;
  rfidModal.value = false;
  rfidCodeData.value = null;
  rfidLastCode.value = "";
  rfidRequestPending.value = false;
}

function enableRFID() {
  latestRequestId += 1;
  rfidModal.value = false;
  rfidCodeData.value = null;
  rfidLastCode.value = "";
  rfidDisabled.value = false;
  rfidRequestPending.value = false;
}
defineExpose({
  disableRFID,
  enableRFID,
});
if (document.scannerDetectionData === undefined) {
  scanner.attachTo(document, {
    onScan: async (scanCode: string) => {
      if (rfidDisabled.value || rfidModal.value || rfidRequestPending.value) {
        return;
      }
      if (scanCode.split("LISAPP").length === 4) {
        if (document.activeElement) {
          // if a User scanned while in focus of a input field it tries to remove the code after the input finished;
          const el = document.activeElement as Element & {
            value: string;
          };
          if (el.value && el.value.includes(scanCode)) {
            el.value = el.value.replace(scanCode, "");
          }
        }

        rfidRequestPending.value = true;
        const requestId = ++latestRequestId;
        try {
          const result = await resolveClient().query({
            query: TCGetStampStateForRfidDocument,
            variables: { code: scanCode },
            fetchPolicy: "no-cache",
          });
          if (
            requestId !== latestRequestId ||
            rfidDisabled.value ||
            result.data.TCStampPreacquisitionData == null
          ) {
            return;
          }
          rfidLastCode.value = scanCode;
          rfidCodeData.value = result.data.TCStampPreacquisitionData;
          rfidModal.value = true;
        } catch {
          if (requestId === latestRequestId) {
            rfidModal.value = false;
            rfidCodeData.value = null;
            rfidLastCode.value = "";
          }
        } finally {
          if (requestId === latestRequestId) {
            rfidRequestPending.value = false;
          }
        }
      }
    },
  });
}

function updateTable(data: TCGetStampStateForRfidQuery["TCStampPreacquisitionData"]) {
  rfidCodeData.value = data;
}
</script>

<style lang="scss" scoped></style>
